/**
 * Offline Status Indicator Component
 * Shows offline status and sync controls
 */

import React from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';

export const OfflineIndicator = ({ compact = false }) => {
  const { 
    online, 
    syncing, 
    syncProgress, 
    pendingCount, 
    stats,
    fullSync 
  } = useOffline();

  if (compact) {
    // Compact version for header/navbar
    return (
      <div className="flex items-center gap-2">
        {online ? (
          <div className="flex items-center gap-1 text-emerald-600">
            <Wifi size={16} />
            {pendingCount > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1 text-amber-600">
            <WifiOff size={16} />
            <span className="text-xs">Offline</span>
          </div>
        )}
      </div>
    );
  }

  // Full version for settings/status page
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-zinc-100">
      {/* Connection Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {online ? (
            <>
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Cloud className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="font-semibold text-zinc-900">Online</p>
                <p className="text-sm text-zinc-500">Connected to server</p>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <CloudOff className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="font-semibold text-zinc-900">Offline</p>
                <p className="text-sm text-zinc-500">Using cached data</p>
              </div>
            </>
          )}
        </div>
        
        {online && (
          <button
            onClick={fullSync}
            disabled={syncing}
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        )}
      </div>

      {/* Sync Progress */}
      {syncing && syncProgress && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">{syncProgress}</p>
        </div>
      )}

      {/* Offline Data Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-zinc-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-zinc-900">{stats.customersCount}</p>
            <p className="text-xs text-zinc-500">Customers</p>
          </div>
          <div className="bg-zinc-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-zinc-900">{stats.jobsCount}</p>
            <p className="text-xs text-zinc-500">Jobs Cached</p>
          </div>
          <div className="bg-zinc-50 rounded-lg p-3 text-center">
            <p className={`text-2xl font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-zinc-900'}`}>
              {pendingCount}
            </p>
            <p className="text-xs text-zinc-500">Pending Sync</p>
          </div>
        </div>
      )}

      {/* Pending Actions Warning */}
      {pendingCount > 0 && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-800">
            <strong>{pendingCount} action{pendingCount > 1 ? 's' : ''}</strong> will sync when you're back online.
          </p>
        </div>
      )}

      {/* Last Sync Info */}
      {stats?.lastSync && (
        <p className="text-xs text-zinc-400 mt-3">
          Last synced: {stats.lastSync}
        </p>
      )}
    </div>
  );
};

// Mini indicator for top bar
export const OfflineBadge = () => {
  const { online, pendingCount } = useOffline();

  if (online && pendingCount === 0) return null;

  return (
    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
      online 
        ? 'bg-amber-100 text-amber-700' 
        : 'bg-red-100 text-red-700'
    }`}>
      {online ? (
        <>
          <RefreshCw size={12} />
          {pendingCount} pending
        </>
      ) : (
        <>
          <WifiOff size={12} />
          Offline
        </>
      )}
    </div>
  );
};

export default OfflineIndicator;
